import { Badge } from "@/components/ui/badge";

const TASK_MAP = {
  Draft: "bg-slate-100 text-slate-600 border-slate-200",
  Assigned: "bg-blue-50 text-blue-700 border-blue-200",
  Ready: "bg-cyan-50 text-cyan-700 border-cyan-200",
  "In Progress": "bg-amber-50 text-amber-700 border-amber-200",
  Submitted: "bg-violet-50 text-violet-700 border-violet-200",
  Approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Accepted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Rejected: "bg-red-50 text-red-700 border-red-200",
  Paid: "bg-slate-900 text-cyan-300 border-cyan-700",
};

const TASK_ADMIN_LABEL = {
  Draft: "Draft",
  Assigned: "Pending acceptance",
  Ready: "Ready to test",
  "In Progress": "In progress",
  Submitted: "Completed",
  Approved: "Approved",
  Accepted: "Approved",
  Rejected: "Rejected",
  Paid: "Paid",
};

const ISSUE_MAP = {
  New: "bg-blue-50 text-blue-700 border-blue-200",
  Open: "bg-sky-50 text-sky-700 border-sky-200",
  Fixed: "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Ready for test": "bg-violet-50 text-violet-700 border-violet-200",
  Rejected: "bg-red-50 text-red-700 border-red-200",
  Cancelled: "bg-slate-100 text-slate-500 border-slate-200",
  Done: "bg-emerald-50 text-emerald-700 border-emerald-200",
  // Legacy — rendered so historical data still looks right.
  Valid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Duplicate: "bg-amber-50 text-amber-700 border-amber-200",
  "Needs More Info": "bg-violet-50 text-violet-700 border-violet-200",
};

const SEV_MAP = {
  critical: "bg-red-100 text-red-700 border-red-200",
  high: "bg-orange-100 text-orange-700 border-orange-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200",
  low: "bg-slate-100 text-slate-600 border-slate-200",
};

const PAY_MAP = {
  Pending: "bg-amber-50 text-amber-700 border-amber-200",
  Approved: "bg-blue-50 text-blue-700 border-blue-200",
  Paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const USER_MAP = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  suspended: "bg-slate-900 text-slate-300 border-slate-700",
};

const PRIORITY_MAP = {
  critical: "bg-red-100 text-red-700 border-red-200",
  high: "bg-orange-100 text-orange-700 border-orange-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200",
  low: "bg-slate-100 text-slate-600 border-slate-200",
};

export function StatusBadge({ kind = "task", value }) {
  const map = { task: TASK_MAP, issue: ISSUE_MAP, severity: SEV_MAP, payment: PAY_MAP, user: USER_MAP, priority: PRIORITY_MAP }[kind] || {};
  const cls = map[value] || "bg-slate-100 text-slate-600 border-slate-200";
  // For task kind, surface admin-facing label per Phase 3A spec.
  const display = kind === "task" ? (TASK_ADMIN_LABEL[value] || value) : value;
  return (
    <span className={`pill ${cls}`} data-testid={`badge-${kind}-${value}`}>
      {display}
    </span>
  );
}

export function LevelBadge({ level }) {
  // Backward-compat wrapper — legacy imports still use LevelBadge.
  return <TierBadge tier={level} />;
}

const TIER_STYLES = {
  Unrated: "bg-slate-100 text-slate-500 border-slate-200",
  "Below Threshold": "bg-red-50 text-red-700 border-red-200",
  Bronze: "bg-amber-50 text-amber-800 border-amber-200",
  Silver: "bg-slate-100 text-slate-700 border-slate-300",
  Gold: "bg-amber-100 text-amber-800 border-amber-300",
  Platinum: "bg-cyan-900 text-cyan-100 border-cyan-700",
};

const TIER_ICON = {
  Unrated: "○",
  "Below Threshold": "▼",
  Bronze: "●",
  Silver: "◆",
  Gold: "★",
  Platinum: "✦",
};

export function TierBadge({ tier, state }) {
  const cls = TIER_STYLES[tier] || TIER_STYLES.Unrated;
  const icon = TIER_ICON[tier] || TIER_ICON.Unrated;
  return (
    <span className={`pill ${cls} inline-flex items-center gap-1`} data-testid={`badge-tier-${tier}`}>
      <span aria-hidden="true">{icon}</span>
      <span>{tier || "Unrated"}{state === "provisional" ? " · Provisional" : ""}</span>
    </span>
  );
}
